import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { HospitalConfig, UserHospital } from '@/types/auth';
import { isAdmin } from '@/utils/role';

import { logout } from '../auth/logout';
import { fetchProfile } from '../profile/profile-api';

export interface HospitalState {
    currentHospitalId: string | null;
    hospitals: UserHospital[];
    hospitalConfig: HospitalConfig | null;
}

const initialState: HospitalState = {
    currentHospitalId: null,
    hospitals: [],
    hospitalConfig: null
};

const hospitalSlice = createSlice({
    name: 'hospital',
    initialState,
    reducers: {
        setCurrentHospitalId: (state, action: PayloadAction<string | null>) => {
            state.currentHospitalId = action.payload;
            const selectedHospital = state.hospitals.find((hospital) => hospital.id === action.payload);
            state.hospitalConfig = selectedHospital?.config ?? state.hospitalConfig;
        },
        setHospitals: (state, action: PayloadAction<UserHospital[]>) => {
            state.hospitals = action.payload;
            if (!state.currentHospitalId) {
                state.currentHospitalId = action.payload[0]?.id ?? null;
            }
        },
        setHospitalConfig: (state, action: PayloadAction<HospitalConfig | null>) => {
            state.hospitalConfig = action.payload;
        },
        clearHospitalState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProfile.fulfilled, (state, { payload }) => {
                const hospitals = Array.isArray(payload.hospitals) ? payload.hospitals : [];

                if (!isAdmin(payload)) {
                    const defaultHospitalId = hospitals.find((hospital) => hospital.isDefault)?.id;
                    state.currentHospitalId = defaultHospitalId ?? hospitals[0]?.id ?? null;
                }

                const selectedHospital = hospitals.find((hospital) => hospital.id === state.currentHospitalId);

                state.hospitals = hospitals;
                state.hospitalConfig = payload.hospitalConfig ?? selectedHospital?.config ?? null;
            })
            .addCase(logout, () => initialState);
    }
});

export const { setCurrentHospitalId, setHospitals, setHospitalConfig, clearHospitalState } = hospitalSlice.actions;
export default hospitalSlice.reducer;
